<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps({
  selectedDayLabel: { type: String, required: true },
  bucketFilters: { type: Object, required: true },
  canSelectResponsible: { type: Boolean, default: false },
  responsibleUserId: { type: [Number, String], default: '' },
  responsibles: { type: Array, default: () => [] },
});

const emit = defineEmits(['setResponsible', 'toggleBucket']);
const { t } = useI18n();
const filters = computed(() => [
  {
    id: 'amarelos',
    label: t('POST_SALE.DEADLINES.AMARELOS'),
    widthClass: 'w-[116px]',
  },
  {
    id: 'laranjas',
    label: t('POST_SALE.DEADLINES.LARANJAS'),
    widthClass: 'w-[121px]',
  },
  {
    id: 'vermelhos',
    label: t('POST_SALE.DEADLINES.VERMELHOS'),
    widthClass: 'w-[122px]',
  },
]);
</script>

<template>
  <header
    data-testid="post-sale-agenda-list-header"
    class="flex flex-wrap items-center justify-between gap-4 border-b border-lais-border-soft bg-white px-8 py-4"
  >
    <p class="text-xl font-bold text-lais-text">
      {{ selectedDayLabel }}
    </p>
    <div class="flex flex-wrap items-center gap-2">
      <fieldset class="flex flex-wrap gap-2">
        <legend class="sr-only">
          {{ t('POST_SALE.AGENDA.FILTERS.TITLE') }}
        </legend>
        <label
          v-for="filter in filters"
          :key="filter.id"
          :data-deadline-filter="filter.id"
          :class="[
            filter.widthClass,
            bucketFilters[filter.id]
              ? 'rounded-[10px] bg-[#EDF8F7] text-[#01988C]'
              : 'text-[#464F53]',
          ]"
          class="flex h-9 items-center gap-1 pl-[15px] font-[Roboto] text-base font-normal leading-6"
        >
          <span
            class="relative -ml-[11px] flex size-9 shrink-0 items-center justify-center"
            data-deadline-filter-control
          >
            <input
              type="checkbox"
              :data-bucket-filter="filter.id"
              :checked="bucketFilters[filter.id]"
              class="peer sr-only"
              @change="emit('toggleBucket', filter.id)"
            />
            <span
              class="pointer-events-none relative flex size-[18px] shrink-0 items-center justify-center before:absolute before:left-[2px] before:top-[2px] before:size-[14px] before:rounded-[2px] before:border-2 before:border-[#01988C] before:bg-transparent before:content-[''] peer-checked:before:border-0 peer-checked:before:bg-[#01988C]"
              data-deadline-filter-icon
              aria-hidden="true"
            />
            <span
              class="i-lucide-check pointer-events-none absolute size-[14px] text-white opacity-0 peer-checked:opacity-100"
              aria-hidden="true"
            />
          </span>
          {{ filter.label }}
        </label>
      </fieldset>
      <label
        v-if="canSelectResponsible"
        data-testid="post-sale-agenda-responsible-filter"
        class="relative block h-[54px] w-60 shrink-0 font-[Roboto]"
      >
        <span
          class="pointer-events-none absolute left-[18px] top-2 z-10 text-[12.8px] leading-[18px] text-[rgba(0,0,0,0.6)]"
        >
          {{ t('POST_SALE.AGENDA.FILTERS.RESPONSIBLE') }}
        </span>
        <select
          :value="responsibleUserId"
          class="!h-[54px] !w-full !appearance-none !rounded-[10px] !border-2 !border-solid !border-[#E0E7EB] !bg-transparent !pb-1 !pl-[17px] !pr-0 !pt-[23px] text-base font-normal leading-[23px] outline-none"
          :class="responsibleUserId ? 'text-[#464F53]' : 'text-[#A7B7BE]'"
          @change="emit('setResponsible', $event.target.value)"
        >
          <option value="" disabled>
            {{ t('POST_SALE.AGENDA.FILTERS.SELECT') }}
          </option>
          <option
            v-for="responsible in responsibles"
            :key="responsible.id"
            :value="responsible.id"
          >
            {{ responsible.name }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute right-[9px] top-[15px] size-6 text-[#464F53]"
          aria-hidden="true"
        />
      </label>
    </div>
  </header>
</template>
